import { useId, type HTMLAttributes, type ReactNode } from 'react';
import styles from './Card.module.css';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** When given, the card is a labelled region with this heading. */
  title?: ReactNode;
  /** Heading level for the title. Default 2. */
  level?: 2 | 3;
  /** Extra controls on the title row (e.g. "See all"). */
  action?: ReactNode;
  padded?: boolean;
  children?: ReactNode;
}

/** The design's `.card`: surface, border, 20px radius and the warm shadow. */
export function Card({
  title,
  level = 2,
  action,
  padded = true,
  className,
  children,
  ...rest
}: CardProps) {
  const headingId = useId();
  const Heading = level === 3 ? 'h3' : 'h2';
  const classes = [styles.card, padded && styles.padded, className].filter(Boolean).join(' ');

  if (!title) {
    return (
      <div className={classes} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <section className={classes} aria-labelledby={headingId} {...rest}>
      <div className={styles.header}>
        <Heading id={headingId} className={styles.title}>
          {title}
        </Heading>
        {action}
      </div>
      {children}
    </section>
  );
}

export interface SectionTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 2 | 3;
  children: ReactNode;
}

/** The design's `.section-title`: small, uppercase, muted. Still a real heading. */
export function SectionTitle({ level = 2, className, children, ...rest }: SectionTitleProps) {
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <Heading className={[styles.sectionTitle, className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </Heading>
  );
}
